import { computed, h } from 'vue'
import { useRoute, type RouteLocationRaw } from 'vue-router'
import { HLText, HLIcon } from '@platform-ui/highrise'
import { useProjectSidebarContext } from '@/composables/useProjectSidebarContext'
import {
  ArrowLeftIcon,
  LogOut01Icon,
  PlusIcon,
  PuzzlePieceIcon,
  Settings01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  MaterialAccessibilityNewFillIcon,
  MaterialAdminPanelSettingsFillIcon,
  MaterialAssignmentIndFillIcon,
  MaterialAutoGraphFillIcon,
  MaterialBugReportIcon,
  MaterialCasesFillIcon,
  MaterialChecklistIcon,
  MaterialDashboardIcon,
  MaterialDiversity3Icon,
  MaterialFeedbackIcon,
  MaterialFolderOpenIcon,
  MaterialGroupsFillIcon,
  MaterialInsertChartFillIcon,
  MaterialPersonIcon,
  MaterialPlayCircleFillIcon,
} from '@gohighlevel/ghl-icons/24/material/rounded'

const createSidebarLogoIcon = (src: string, alt: string) => () =>
  h('img', {
    src,
    alt,
    class: 'h-5 w-5 rounded-sm object-contain',
  })

export interface NavItem {
  key: string
  label: string
  icon: any
  to: RouteLocationRaw
  permission?: string
  badge?: string | number
  children?: NavItem[]
}

export interface SidebarHeader {
  type: 'global' | 'project'
  title: string
  subtitle?: string
  backTo?: string
}

/** Icon map for easy lookup */
const ICONS = {
  bugs: MaterialBugReportIcon,
  dashboard: MaterialDashboardIcon,
  projects: MaterialFolderOpenIcon,
  teams: MaterialGroupsFillIcon,
  reports: MaterialInsertChartFillIcon,
  integrations: PuzzlePieceIcon,
  settings: Settings01Icon,
  logout: LogOut01Icon,
  plus: PlusIcon,
  features: MaterialChecklistIcon,
  testRuns: MaterialPlayCircleFillIcon,
  testCases: MaterialCasesFillIcon,
  overview: MaterialAutoGraphFillIcon,
  back: ArrowLeftIcon,
  myTasks: MaterialAssignmentIndFillIcon,
  crossTeamSanity: MaterialDiversity3Icon,
  accessibility: MaterialAccessibilityNewFillIcon,
  users: MaterialPersonIcon,
  userManagement: MaterialAdminPanelSettingsFillIcon,
  kaizenFeedback: MaterialFeedbackIcon,
} as const

export type IconName = keyof typeof ICONS

export function getIcon(name: IconName) {
  return ICONS[name]
}


export function useSidebarNav() {
  const route = useRoute()
  const { activeProjectId, activeProjectTitle } = useProjectSidebarContext()

  const projectId = computed(() => route.params.projectId as string | undefined)
  const isProjectContext = computed(() => !!projectId.value)

  const header = computed<SidebarHeader>(() => {
    if (isProjectContext.value) {
      const id = projectId.value as string
      const fromContext =
        activeProjectId.value === id && activeProjectTitle.value ? activeProjectTitle.value : null
      const title =
        fromContext ||
        (route.meta?.projectName as string | undefined) ||
        'Project'
      return {
        type: 'project',
        title,
        backTo: '/dashboard',
      }
    }
    return {
      type: 'global',
      title: 'Kaizen',
      subtitle: 'Test Manager',
    }
  })

  const navItems = computed<NavItem[]>(() => {
    if (isProjectContext.value) {
      const base = `/projects/${projectId.value}`
      return [
        { key: 'overview', label: 'Overview', icon: ICONS.overview, to: `${base}/overview` },
        { key: 'features', label: 'Features', icon: ICONS.features, to: `${base}/features` },
        { key: 'test-runs', label: 'Test Runs', icon: ICONS.testRuns, to: `${base}/test-runs` },
        { key: 'test-cases', label: 'Test Suites', icon: ICONS.testCases, to: `${base}/test-cases` },
        { key: 'bugs', label: 'Bugs', icon: ICONS.bugs, to: `${base}/bugs` },
        { key: 'users', label: 'Members', icon: ICONS.users, to: `${base}/users` },
        // { key: 'settings', label: 'Settings', icon: ICONS.settings, to: `${base}/settings`, permission: 'viewSettings' },
      ]
    }

    return [
      { key: 'dashboard', label: 'Dashboard', icon: ICONS.dashboard, to: '/dashboard' },
      { key: 'my-tasks', label: 'My Tasks', icon: ICONS.myTasks, to: '/my-tasks' },
      { key: 'projects', label: 'Projects', icon: ICONS.projects, to: '/projects' },
      { key: 'teams', label: 'Verticals', icon: ICONS.teams, to: '/teams' },
      { key: 'cross-team-sanity', label: 'Cross Vertical Sanity', icon: ICONS.crossTeamSanity, to: '/cross-team-sanity' },
      // { key: 'integrations', label: 'Integrations', icon: ICONS.integrations, to: '/integrations' },
      { key: 'reports', label: 'Reports', icon: ICONS.reports, to: '/reports' },
      { key: 'accessibility', label: 'Accessibility', icon: ICONS.accessibility, to: '/accessibility' },
      { key: 'user-management', label: 'Users', icon: ICONS.userManagement, to: '/users', permission: 'viewUsers' },
      { key: 'kaizen-feedback', label: 'Kaizen Feedback', icon: ICONS.kaizenFeedback, to: '/feedback', permission: 'viewKaizenFeedback' },
    ]
  })

  const footerItems = computed<NavItem[]>(() => {
    const items: NavItem[] = []
    items.push({ key: 'logout', label: 'Logout', icon: ICONS.logout, to: '' })
    return items
  })

  /** Convert NavItems to HLMenu MenuOption format */
  function toMenuOptions(items: NavItem[]) {
    return items.map((item) => ({
      key: item.key,
      label: () => h(HLText, { id: `menu-${item.key}`, size: 'lg', weight: 'regular', htmlTag: 'span' }, () => item.label),
      icon: () => h(HLIcon, { size: 20}, () => h(item.icon)),
      count: item.badge,
      children: item.children ? toMenuOptions(item.children) : undefined,
    }))
  }

  const menuOptions = computed(() => toMenuOptions(navItems.value))
  const footerMenuOptions = computed(() => toMenuOptions(footerItems.value))

  /** Lookup map: menu key → route path */
  const routeMap = computed(() => {
    const map: Record<string, RouteLocationRaw> = {}
    const collectRoutes = (items: NavItem[]) => {
      for (const item of items) {
        map[item.key] = item.to
        if (item.children?.length) {
          collectRoutes(item.children)
        }
      }
    }
    collectRoutes(navItems.value)
    collectRoutes(footerItems.value)
    return map
  })

  const activeKey = computed<string>(() => {
    const path = route.path

    if (isProjectContext.value) {
      const pid = projectId.value as string
      const projectRoot = `/projects/${pid}`
      if (path.includes('/features')) return 'features'
      if (path.includes('/test-runs')) return 'test-runs'
      if (path.includes('/test-cases')) return 'test-cases'
      if (path.includes('/bugs')) return 'bugs'
      if (path.includes('/users')) return 'users'
      if (path.includes('/settings')) return 'settings'
      if (
        path === projectRoot ||
        path === `${projectRoot}/` ||
        path === `${projectRoot}/overview`
      ) {
        return 'overview'
      }

      return 'overview'
    }

    if (path === '/projects') return 'projects'
    if (path.startsWith('/my-tasks') || path.startsWith('/assigned-to-me')) return 'my-tasks'
    if (path.startsWith('/cross-team-sanity')) return 'cross-team-sanity'
    if (path.startsWith('/reports')) return 'reports'
    if (path.startsWith('/accessibility')) return 'accessibility'
    if (path.startsWith('/integrations') || path.startsWith('/jenkins') || path.startsWith('/github') || path.startsWith('/slack') || path.startsWith('/chrome-extension')) return 'integrations'
    if (path.startsWith('/teams')) return 'teams'
    if (path.startsWith('/users')) return 'user-management'
    if (path.startsWith('/feedback')) return 'kaizen-feedback'
    if (path.startsWith('/profile')) return 'settings'
    return 'dashboard'
  })

  return {
    header,
    navItems,
    footerItems,
    menuOptions,
    footerMenuOptions,
    routeMap,
    activeKey,
    isProjectContext,
    projectId,
  }
}
